// The boat's boards as the shell lists them (TOM-192, boards-v3.md "Your
// boards and the sidebar"): the list from `GET /v1/boats/:boatId/boards`, and
// which boards take the bar's slots on a phone or tablet and which go under
// "+N". Pure; useNeoBoards.ts reads and refreshes it.

import {
  NEO_SYSTEM_BOARD_KEYS,
  isNeoSystemBoardKey,
  type NeoBoardSummary as ProtocolBoardSummary,
} from '@neomaris/protocol';

/**
 * A board in the list, with who made it and when (TOM-379, api_contracts
 * §5.13): `neo` or `owner`, set once when the board was made, and null when
 * that is unknown (a board made before it was recorded, or a system board).
 */
export type NeoBoardSummary = ProtocolBoardSummary & {
  madeBy: 'owner' | 'neo' | null;
  createdAt: string | null;
};

/**
 * The boat's boards as the web lists them: the owner's boards in the boat's
 * order, and the system boards apart, in their fixed order (cloud ADR 0305 §1).
 * Only owner boards take part in the order and its `orderVersion`.
 */
export interface NeoBoardListView {
  boards: NeoBoardSummary[];
  systemBoards: NeoBoardSummary[];
  orderVersion: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * The list route's body, or null when it is not one. A malformed row is
 * dropped. A system board is one only with its key; it is found by that key,
 * never by its slug.
 */
export function parseNeoBoardList(raw: unknown): NeoBoardListView | null {
  if (!isRecord(raw) || !Array.isArray(raw.boards)) return null;
  const boards: NeoBoardSummary[] = [];
  const systemBoards: NeoBoardSummary[] = [];
  for (const b of raw.boards) {
    if (!isRecord(b)) continue;
    const { slug, title, position, version, updatedAt, itemCount } = b;
    if (typeof slug !== 'string' || slug === '' || typeof title !== 'string') continue;
    if (typeof position !== 'number' || typeof version !== 'number') continue;
    const summary: NeoBoardSummary = {
      slug,
      title,
      position,
      version,
      updatedAt: typeof updatedAt === 'string' ? updatedAt : null,
      itemCount: typeof itemCount === 'number' ? itemCount : 0,
      madeBy: b.madeBy === 'owner' || b.madeBy === 'neo' ? b.madeBy : null,
      createdAt: typeof b.createdAt === 'string' ? b.createdAt : null,
    };
    if (b.origin === 'system' && isNeoSystemBoardKey(b.systemKey)) {
      systemBoards.push({ ...summary, origin: 'system', systemKey: b.systemKey });
    } else {
      boards.push({ ...summary, origin: 'owner', systemKey: null });
    }
  }
  boards.sort((a, b) => a.position - b.position);
  const rank = (b: NeoBoardSummary) => NEO_SYSTEM_BOARD_KEYS.indexOf(b.systemKey!);
  systemBoards.sort((a, b) => rank(a) - rank(b));
  return {
    boards,
    systemBoards,
    orderVersion: typeof raw.orderVersion === 'number' ? raw.orderVersion : 0,
  };
}

/** Board slots on the bar: two on a phone, three on a tablet (boards-v3.md, inventory §3.3). */
export function barBoardSlots(phone: boolean): number {
  return phone ? 2 : 3;
}

export interface BarBoards {
  /** The owner's boards on the bar, in slot order. */
  slotted: NeoBoardSummary[];
  /** The ones under "+N", in the boat's order. */
  more: NeoBoardSummary[];
}

/**
 * Which of the owner's boards take the bar's slots. Slots rank the Alarm while
 * active, the mode board, the newest owner board, then the rest in the boat's
 * order (boards-v3.md). The Alarm has its own place on the web's bar, and the
 * mode board is TOM-358's; until then the Dashboard holds that slot, so
 * `reserved` slots are taken before the owner's boards.
 */
export function barBoards(
  boards: readonly NeoBoardSummary[],
  slots: number,
  reserved: number
): BarBoards {
  const free = Math.max(0, slots - reserved);
  if (free === 0) return { slotted: [], more: [...boards] };
  const newest = [...boards].sort((a, b) => updatedMs(b) - updatedMs(a))[0];
  const rest = boards.filter((b) => b !== newest);
  const slotted = newest === undefined ? [] : [newest, ...rest].slice(0, free);
  return { slotted, more: boards.filter((b) => !slotted.includes(b)) };
}

function updatedMs(board: NeoBoardSummary): number {
  const ms = board.updatedAt === null ? NaN : Date.parse(board.updatedAt);
  return Number.isFinite(ms) ? ms : -Infinity;
}

/**
 * Your boards' line for a board Neo made (boards-v3.md): "Made by Neo 2:41 PM"
 * on the day it was made, and the date after that. A board an owner made, or
 * one whose maker is unknown, has no line.
 */
export function madeByText(
  board: Pick<NeoBoardSummary, 'madeBy' | 'createdAt'>,
  now: number = Date.now(),
  locale?: string
): string | null {
  if (board.madeBy !== 'neo' || board.createdAt === null) return null;
  const made = new Date(board.createdAt);
  if (Number.isNaN(made.getTime())) return null;
  const today = new Date(now);
  const sameDay =
    made.getFullYear() === today.getFullYear() &&
    made.getMonth() === today.getMonth() &&
    made.getDate() === today.getDate();
  const when = sameDay
    ? made.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
    : made.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  return `Made by Neo ${when}`;
}

/** "3 items", "1 item", "Empty". */
export function boardItemCountText(count: number): string {
  if (count === 0) return 'Empty';
  return count === 1 ? '1 item' : `${count} items`;
}
